import {NativeAudio} from '@ionic-native/native-audio';
import {NgZone, ViewChild} from '@angular/core';
import {
  Alert,
  AlertController,
  AlertOptions,
  Content,
  Events,
  Loading,
  LoadingController,
  LoadingOptions,
  NavController,
  Platform,
  Select
} from 'ionic-angular';
import {StatusBar} from '@ionic-native/status-bar';
import {InsomniaService} from '../providers/insomnia.service';
import {TranslateService} from '@ngx-translate/core';
import {VizixService} from '../providers/vizix.service';
import {VizixEventService} from '../providers/vizix.event.service';
import {Keyboard} from '@ionic-native/keyboard';
import {AssociationConfiguration} from './association.config';
import {ManageTagsI} from '../interfaces/manage-tags.i';
import {ZoneType} from './zoneDeptRelation';
import {AudioConfig} from '../pages/manage-tags/manage-tags';
import {AppConfigPage} from '../pages/app-config/app-config';
import {Observable} from 'rxjs/Observable';
import {Observer} from 'rxjs/Observer';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/finally';
import {EncodeTagConfiguration} from "./encodeTag.config";
import {NAV_PARAM_RETURN_PAGE} from "../app/app.constants";
import any = jasmine.any;

export class ManageTagsBase implements ManageTagsI {
  returnPage: string;
  isAlert: boolean = false;
  tag: string;
  TAG_TYPE: string;
  thingTypeCodeToSyncGroup: string;
  firstThingTypeCodeGroup: string;
  loading: Loading;
  config: AssociationConfiguration | EncodeTagConfiguration | any;
  firstField: string;
  secondField: string;
  alert: Alert;
  selectedZone: ZoneType;
  listZones: ZoneType[];
  showFooterClassCss: string;
  username: string;
  uuid: string;
  soundAssociation: string;
  soundError: string;
  soundSuccess;
  isSoundAssociationReady: boolean;
  isSoundErrorReady: boolean;
  isSoundSuccessReady: boolean;
  @ViewChild(Content) content: Content;
  @ViewChild('selectZone') selectZone: Select;
  /**
   * Handles keyboard show event
   */
  handleKeyboardShow = () => {
    this.showFooterClassCss = 'hideKeyBoard';
    this.showFooterClassCss = 'hideKeyBoard';
    this.content.resize();
  };
  /**
   * Handles keyboard hide event
   */
  handleKeyboardHide = () => {
    this.showFooterClassCss = 'showKeyBoard';
    this.content.resize();
  };
  /**
   * Insomnia successful event subscription
   * @param response
   */
  handleInsomniaSuccessfulEvents = (response: any) => {
    console.log('[INSOMNIA PLUGIN]', response);
  };
  /**
   * Insomnia error event subscription
   * @param error
   */
  handleInsomniaErrorEvents = (error: any) => {
    console.error('[INSOMNIA PLUGIN]', error);
  };

  /**
   * Manage Tags Base Page
   *
   * @param {NavController} navCtrl
   * @param {VizixService} vizixService
   * @param {VizixEventService} vizixEventService
   * @param {StatusBar} statusBar
   * @param {AlertController} alertCtrl
   * @param {Platform} platform
   * @param {LoadingController} loadingCtrl
   * @param {Keyboard} keyBoard
   * @param {TranslateService} translate
   * @param {NgZone} ngZone
   * @param {Events} events
   * @param {NativeAudio} nativeAudio
   * @param {InsomniaService} insomniaService
   */
  constructor(public navCtrl: NavController,
              public vizixService: VizixService,
              public vizixEventService: VizixEventService,
              public statusBar: StatusBar,
              public alertCtrl: AlertController,
              public platform: Platform,
              public loadingCtrl: LoadingController,
              public keyBoard: Keyboard,
              public translate: TranslateService,
              public ngZone: NgZone,
              public events: Events,
              public nativeAudio: NativeAudio,
              public insomniaService: InsomniaService) {
    this.initializeVariables();
  }

  /**
   * Initialize class property values
   */
  initializeVariables() {
    this.firstField = '';
    this.secondField = '';
    this.soundAssociation = 'trackAssociation';
    this.soundError = 'trackError';
    this.soundSuccess = 'trackSuccess';
    this.showFooterClassCss = 'showKeyBoard';
    this.listZones = this.vizixService.getFirstDifferentsZones(false);
  }

  /**
   * When view is staring to load hook
   * Hook #1
   */
  ionViewWillEnter() {
    this.registerBackButtonAction();
  }

  /**
   * When view loads
   * Hook #2
   */
  ionViewDidLoad() {
    this.setDefaultDeptAndZone();
    this.loadConfigFieldValues();
    this.loadUserProfile();

  }

  /**
   * When view completely renders
   * Hook #3
   */
  ionViewDidEnter() {
    this.initializeSounds();
    this.subscribeEvents();
    this.events.publish('upcEpcGrid:firstFieldFocus');
  }

  /**
   * When view is staring to unload hook
   * Hook #4
   */
  ionViewWillLeave() {
    this.unSubscribeEvents();
    this.unloadSounds();
  }

  /**
   * Update style of status bar for this view
   */
  setStatusBarStyle() {
  }

  /**
   * Custom action for hardware back button action (Android Only)
   */
  registerBackButtonAction() {

    const handleHardwareBackButton = () => {
      this.selectZone.close();
      if (!this.isAlert && !(this.loading instanceof Loading)) {
        this.navCtrl.pop();
      } else {
        if (this.alert instanceof Alert) {
          this.dismissAlert();
        }
      }
    };

    this.platform.registerBackButtonAction(handleHardwareBackButton);
  }

  /**
   * Subscribe to the view events
   */
  subscribeEvents() {
    if (this.platform.is('cordova')) {
      this.insomniaService.insomniaKeepAwakeScreen().subscribe(this.handleInsomniaSuccessfulEvents, this.handleInsomniaErrorEvents);
      this.keyBoard.onKeyboardShow().subscribe(this.handleKeyboardShow);
      this.keyBoard.onKeyboardHide().subscribe(this.handleKeyboardHide);
    }
  }

  /**
   * Unsubscribe to view events when starting to unload the view
   */
  unSubscribeEvents() {
    if (this.platform.is('cordova')) {
      this.insomniaService.insomniaAllowScreenSleepAgain()
        .subscribe(this.handleInsomniaSuccessfulEvents, this.handleInsomniaErrorEvents);
    }
  }

  /**
   * Get the username and uuid, possibly other values, to send to patchItem
   */
  loadUserProfile(): Promise<{ user: string, remoteSource: string }> {
    const promise = this.vizixEventService.getVizixEventItemBase();
    promise.then((event: { user: string, remoteSource: string }) => {
      this.username = event.user;
      this.uuid = event.remoteSource;
    });
    return promise;
  }

  /**
   * Set default deptartment and zone
   */
  setDefaultDeptAndZone() {
    let defaultZone;
    if (this.config instanceof AssociationConfiguration) {
      defaultZone = this.config.DefaultAssociationZone.value;
    }
    else {
      //defaultZone= this.config.DefaultAssociationZone.value;
      defaultZone = this.config.DefaultEncodeZone.value;
    }

    for (let zone of this.listZones) {
      if (zone.zoneTypeCode == defaultZone) {
        this.selectedZone = zone;
        break;
      }
    }
  }

  /**
   * Load config field values
   */
  loadConfigFieldValues(): Observable<Array<string>> {
    return Observable.create((observer: Observer<Array<string>>) => {
      Observable.forkJoin([
        this.vizixService.VizixGetThingTypeGroup(this.config.ThingTypeCodeToSync.value),
        this.vizixService.VizixGetThingTypeGroup(this.config.FirstThingTypeCode.value)
      ])
        .finally(() => observer.complete())
        .subscribe(
          (results: Array<string>) => {
            this.thingTypeCodeToSyncGroup = results[0];
            this.firstThingTypeCodeGroup = results[1];
            observer.next(results);
          },
          (error) => observer.error(error)
        );
    });
  }

  /**
   * Configure sounds for method event results
   */
  initializeSounds(): Promise<Array<any>> {
    const audios: Array<AudioConfig> = [
      {
        id: this.soundAssociation,
        assetPath: 'assets/sounds/association.wav',
        volume: 1,
        voices: 1,
        delay: 0,
        resolve: () => this.isSoundAssociationReady = true
      },
      {
        id: this.soundError,
        assetPath: 'assets/sounds/error.wav',
        volume: 1,
        voices: 1,
        delay: 0,
        resolve: () => this.isSoundErrorReady = true
      },
      {
        id: this.soundSuccess,
        assetPath: 'assets/sounds/success.mp3',
        volume: 1,
        voices: 1,
        delay: 0,
        resolve: () => this.isSoundSuccessReady = true
      }
    ];
    let promises: Promise<any>[] = [];
    audios.forEach((audio: AudioConfig) => {
      if (this.platform.is('cordova')) {
        promises.push(this.nativeAudio.preloadComplex(audio.id, audio.assetPath, audio.volume, audio.voices, audio.delay).then(audio.resolve));
      }
    });
    return Promise.all(promises);
  }

  unloadSounds() {
    this.nativeAudio.unload(this.soundAssociation);
    this.nativeAudio.unload(this.soundError);
    this.nativeAudio.unload(this.soundSuccess);
  }

  /**
   * Go to the config app view
   */
  goToAppConfig(): Promise<any> {
    let returnNavParams = {};
    returnNavParams[NAV_PARAM_RETURN_PAGE] = this.returnPage;
    return this.navCtrl.push(AppConfigPage, returnNavParams);
  }

  /**
   * Go back to previous view
   */
  goBack(): Promise<any> {
    return this.navCtrl.pop();
  }

  /**
   * Dismiss alert if opened
   */
  dismissAlert(): Promise<any> {
    if (this.isAlert) {
      this.isAlert = false;
      return this.alert.dismiss();
    }
  }

  /**
   * Dismiss loading association message
   */
  dismissLoading(): Promise<any> {
    if (this.loading instanceof Loading) {
      return this.loading.dismiss();
    }
  }

  /**
   * Reset form
   */
  resetForm() {
    this.firstField = '';
    this.secondField = '';
    this.TAG_TYPE = this.config.FirstFieldLabel.value;
    this.events.publish('upcEpcGrid:firstFieldFocus');
  }

  /**
   * A centralized alert manager
   * @param {any} params
   * @returns {boolean}
   */
  onAlert(params: { type: 'alert' | 'cancel' | 'error' | 'dismiss', title: string, message: string, okButtonText?: string, cancelButtonText?: string, callback?: (value: any) => void }) {
    let alertOptions: AlertOptions = {
      title: params.title,
      message: params.message,
      enableBackdropDismiss: false,
      buttons: [
        {
          text: params.okButtonText,
          handler: params.callback
        }
      ]
    };
    switch (params.type) {
      case 'alert':
      case 'cancel':
        this.nativeAudio.play(this.soundSuccess);
        if (params.type === 'cancel') {
          alertOptions.buttons.unshift({
            text: params.cancelButtonText,
            role: 'cancel',
            handler: () => {
              this.resetForm();
              if (params.callback) {
                params.callback;
              }
            }
          });
        }
        break;
      case 'error':
        this.nativeAudio.play(this.soundError);
        break;
      case 'dismiss':
        this.dismissAlert();
        return false;
    }
    this.alert = this.alertCtrl.create(alertOptions);
    this.isAlert = true;
    this.alert.present();
  }

  /**
   * A centralized loading manager
   * @param {LoadingOptions} params
   * @returns {boolean}
   */
  onLoading(params: LoadingOptions): Promise<any> {
    this.loading = this.loadingCtrl.create(params);
    this.loading.onDidDismiss(() => this.loading = null);
    return this.loading.present();
  }

}



// WEBPACK FOOTER //
// ./src/models/ManageTagsBase.ts